/**
 * UtmBuilder — Constructor de URLs trackeables (UTM).
 *
 * Permite construir URLs con parámetros utm_source, utm_medium, utm_campaign,
 * utm_term y utm_content para trackear el origen del tráfico en Google
 * Analytics / Meta Pixel / Mixpanel / etc.
 *
 * - Genera vista previa en vivo (preview backend para encoding correcto).
 * - Permite guardar el link en el historial compartido del equipo.
 * - Lista, copia y elimina links guardados.
 */
import { useState, useEffect, useCallback } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Link2, Copy, Save, Trash2, Sparkles, BookOpen, ExternalLink } from 'lucide-react';

const HELP = {
  utm_source: { ej: 'newsletter, facebook, whatsapp, google', tip: 'De dónde viene el visitante (la fuente concreta).' },
  utm_medium: { ej: 'email, cpc, social, organic, sms', tip: 'El tipo de canal/medio publicitario.' },
  utm_campaign: { ej: 'lanzamiento-mayo, black-friday-2026', tip: 'Nombre de la campaña que está corriendo.' },
  utm_term: { ej: 'casa-cdmx, suv-2024', tip: 'Palabra clave o término (opcional, usado en paid search).' },
  utm_content: { ej: 'banner-azul, cta-arriba, version-a', tip: 'Variante creativa (opcional, útil para A/B testing).' },
};

const SUGGESTED = {
  source: ['newsletter', 'facebook', 'instagram', 'whatsapp', 'google', 'linkedin', 'youtube'],
  medium: ['email', 'social', 'cpc', 'organic', 'referral', 'sms', 'push'],
};

const empty = { label: '', base_url: '', utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' };

export default function UtmBuilder() {
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState('');
  const [history, setHistory] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadHistory = useCallback((append = false, cursor = null) => {
    if (!append) setLoadingHistory(true);
    const params = { limit: 25 };
    if (cursor) params.cursor = cursor;
    api.get('/utm-links', { params })
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const nc = Array.isArray(r.data) ? null : (r.data?.next_cursor || null);
        setHistory(prev => append ? [...prev, ...items] : items);
        setNextCursor(nc);
      })
      .catch(() => { if (!append) setHistory([]); })
      .finally(() => { setLoadingHistory(false); setLoadingMore(false); });
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    loadHistory(true, nextCursor);
  };

  // Preview con debounce (genera URL final en backend para encoding correcto)
  useEffect(() => {
    const required = form.base_url && form.utm_source && form.utm_medium && form.utm_campaign;
    if (!required) { setPreview(''); return; }
    const t = setTimeout(() => {
      api.post('/utm-links/preview', { ...form, label: form.label || 'preview' })
        .then(r => setPreview(r.data.final_url))
        .catch(() => setPreview(''));
    }, 350);
    return () => clearTimeout(t);
  }, [form]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const copy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('URL copiada al portapapeles');
    } catch {
      // Fallback: usar el método legacy si Clipboard API está bloqueado (sandbox, http inseguro, etc.)
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('URL copiada al portapapeles');
      } catch {
        toast.info('No se pudo copiar automáticamente. Selecciona y copia manualmente la URL.');
      }
    }
  };

  const reset = () => { setForm(empty); setPreview(''); };

  const save = async () => {
    if (!form.label.trim()) { toast.error('Ponle un nombre interno al link'); return; }
    if (!form.base_url || !form.utm_source || !form.utm_medium || !form.utm_campaign) {
      toast.error('Completa URL, source, medium y campaign');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/utm-links', form);
      toast.success('Link guardado en el historial');
      await copy(r.data.final_url);
      reset();
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando link');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este link del historial?')) return;
    try {
      await api.delete(`/utm-links/${id}`);
      toast.success('Eliminado');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-5" data-testid="utm-builder">
      {/* Explicación amigable */}
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="p-4 flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-amber-300" />
          </div>
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">¿Para qué sirven los UTMs?</p>
            <p className="text-white/70 text-xs leading-relaxed">
              Los UTMs son <span className="text-amber-300 font-semibold">etiquetas que pegas a una URL</span> para
              saber <span className="text-amber-300 font-semibold">de dónde vino cada visita</span>. Si mandas el mismo link
              por email, WhatsApp y Facebook con UTMs distintos, Google Analytics (y tus reportes de {APP_NAME}) te dirán
              cuál canal trajo más leads o ventas. <span className="text-amber-200">Sin UTMs, no sabes qué campaña funcionó.</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Formulario */}
      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Link2 className="w-4 h-4 text-amber-400" /> Constructor de URL trackeable
          </CardTitle>
          <CardDescription className="text-xs text-white/60">
            Llena los campos. La URL final se genera abajo en vivo. Solo source, medium y campaign son obligatorios.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-white/80">Nombre interno *</Label>
              <Input
                value={form.label}
                onChange={e => update('label', e.target.value)}
                placeholder='Ej. "Email mayo - cta principal"'
                className="bg-black/20 border-white/10"
                data-testid="utm-label"
              />
              <p className="text-[10px] text-white/40">Te ayuda a encontrar este link después en el historial.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/80">URL destino *</Label>
              <Input
                value={form.base_url}
                onChange={e => update('base_url', e.target.value)}
                placeholder="https://miempresa.com/landing"
                className="bg-black/20 border-white/10 font-mono text-xs"
                data-testid="utm-base-url"
              />
              <p className="text-[10px] text-white/40">A dónde irá el visitante cuando haga clic.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <UtmField name="utm_source" label="Source (fuente) *" value={form.utm_source} onChange={v => update('utm_source', v)} required />
            <UtmField name="utm_medium" label="Medium (medio) *" value={form.utm_medium} onChange={v => update('utm_medium', v)} required />
            <UtmField name="utm_campaign" label="Campaign (campaña) *" value={form.utm_campaign} onChange={v => update('utm_campaign', v)} required />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <UtmField name="utm_term" label="Term (opcional)" value={form.utm_term} onChange={v => update('utm_term', v)} />
            <UtmField name="utm_content" label="Content (opcional)" value={form.utm_content} onChange={v => update('utm_content', v)} />
          </div>

          {/* Sugerencias rápidas */}
          {!form.utm_source && !form.utm_medium && (
            <div className="p-3 rounded-lg bg-black/20 border border-white/5">
              <p className="text-[10px] text-white/50 uppercase mb-2">Sugerencias rápidas</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                <span className="text-[10px] text-white/40 w-16">source:</span>
                {SUGGESTED.source.map(s => (
                  <button key={s} onClick={() => update('utm_source', s)} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/70 hover:bg-amber-400/20 hover:text-amber-200" data-testid={`utm-suggest-source-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] text-white/40 w-16">medium:</span>
                {SUGGESTED.medium.map(s => (
                  <button key={s} onClick={() => update('utm_medium', s)} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/70 hover:bg-amber-400/20 hover:text-amber-200" data-testid={`utm-suggest-medium-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Vista previa */}
          <div className="space-y-2">
            <Label className="text-xs text-white/80 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-amber-300" /> URL final generada
            </Label>
            <div className="p-3 rounded-lg bg-black/30 border border-white/10 min-h-[58px] flex items-center justify-between gap-2">
              <code className="text-[11px] text-emerald-300 font-mono break-all flex-1" data-testid="utm-preview">
                {preview || <span className="text-white/30">Completa los campos obligatorios para ver la URL…</span>}
              </code>
              {preview && (
                <Button size="sm" variant="outline" onClick={() => copy(preview)} className="border-white/10 text-white/80 shrink-0" data-testid="utm-copy-preview-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving || !preview} className="bg-amber-500 hover:bg-amber-500/90 text-black font-bold" data-testid="utm-save-btn">
              <Save className="w-4 h-4 mr-1" /> Guardar en historial y copiar
            </Button>
            <Button onClick={() => copy(preview)} disabled={!preview} variant="outline" className="border-white/10" data-testid="utm-copy-only-btn">
              <Copy className="w-4 h-4 mr-1" /> Solo copiar (sin guardar)
            </Button>
            <Button onClick={reset} variant="ghost" className="text-white/60" data-testid="utm-reset-btn">
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Historial */}
      <Card className="bg-card/40 border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm text-white flex items-center gap-2">
            <Link2 className="w-4 h-4 text-amber-400" /> Historial del equipo
          </CardTitle>
          <CardDescription className="text-xs text-white/60">
            Links guardados visibles para todo el equipo. Reutiliza UTMs que ya estandarizaste.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <p className="text-xs text-white/40 text-center py-6">Cargando…</p>
          ) : history.length === 0 ? (
            <div className="py-8 text-center" data-testid="utm-history-empty">
              <Link2 className="w-10 h-10 mx-auto text-white/15 mb-2" />
              <p className="text-xs text-white/50">No hay links guardados todavía.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="p-3 rounded-lg bg-black/20 border border-white/5 hover:border-amber-400/20 transition" data-testid={`utm-history-${h.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{h.label}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant="outline" className="text-[9px] border-amber-400/30 text-amber-300">{h.utm_source}</Badge>
                        <Badge variant="outline" className="text-[9px] border-cyan-400/30 text-cyan-300">{h.utm_medium}</Badge>
                        <Badge variant="outline" className="text-[9px] border-violet-400/30 text-violet-300">{h.utm_campaign}</Badge>
                        {h.utm_term && <Badge variant="outline" className="text-[9px] border-white/15 text-white/60">term: {h.utm_term}</Badge>}
                        {h.utm_content && <Badge variant="outline" className="text-[9px] border-white/15 text-white/60">content: {h.utm_content}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => copy(h.final_url)} className="h-7 w-7 p-0 text-white/60 hover:text-amber-300" data-testid={`utm-copy-${h.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <a href={h.final_url} target="_blank" rel="noopener noreferrer" className="h-7 w-7 p-0 inline-flex items-center justify-center text-white/60 hover:text-cyan-300" data-testid={`utm-open-${h.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => remove(h.id)} className="h-7 w-7 p-0 text-white/40 hover:text-rose-400" data-testid={`utm-delete-${h.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <code className="text-[10px] text-emerald-300/80 font-mono break-all block">{h.final_url}</code>
                </div>
              ))}
              {nextCursor && (
                <div className="text-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="border-white/10 text-white/70 hover:text-amber-200 text-xs"
                    data-testid="utm-load-more-btn"
                  >
                    {loadingMore ? 'Cargando…' : 'Cargar más antiguos'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UtmField({ name, label, value, onChange, required }) {
  const h = HELP[name];
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-white/80">{label}</Label>
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={h?.ej?.split(',')[0]?.trim() || ''}
        className="bg-black/20 border-white/10 text-xs"
        data-testid={`utm-${name}`}
      />
      {h && (
        <p className="text-[10px] text-white/40 leading-snug">
          {h.tip} <span className="text-white/30">Ej: {h.ej}</span>
        </p>
      )}
    </div>
  );
}
